// PublicSpaceRoute.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SpaceModal from './SpaceModal';
import { API_BASE_URL } from '../config';

const PublicSpaceRoute = () => {
  const { spaceIdSupernumber } = useParams();
  const [isVerified, setIsVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [spaceId, setSpaceId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyAccess = async () => {
      try {
        // Parse spaceId-supernumber format (e.g., "1308-24880761612")
        const parts = spaceIdSupernumber.split('-');
        if (parts.length !== 2) {
          throw new Error('Invalid URL format. Expected: spaceId-supernumber');
        }

        const parsedSpaceId = parseInt(parts[0], 10);
        const supernumber = parseInt(parts[1], 10);

        if (isNaN(parsedSpaceId) || isNaN(supernumber)) {
          throw new Error('Invalid space ID or verification code');
        }

        // Verify access with backend using axios
        const response = await axios.post(`${API_BASE_URL}/api/post`, {
          function_name: 'verifySpaceAccess',
          payload: {
            spaceId: parsedSpaceId,
            supernumber: supernumber,
          },
        });

        if (response.data.isValid) {
          setSpaceId(parsedSpaceId); // Set as INTEGER
          setIsVerified(true);
        } else {
          throw new Error('Access verification failed');
        }
      } catch (err) {
        console.error('Verification error:', err);
        setError(err.response?.data?.error || err.message || 'Failed to verify access');
      } finally {
        setIsVerifying(false);
      }
    };

    if (spaceIdSupernumber) {
      verifyAccess();
    }
  }, [spaceIdSupernumber]);

  if (isVerifying) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (error || !isVerified) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="text-red-600 text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            {error || 'You do not have permission to view this space.'}
          </p>
          <p className="text-sm text-gray-500">
            Please check your link and try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen">
      <div className="flex items-center gap-2 text-blue-600">
        <span>🌐</span>
      </div>
      <SpaceModal 
        isOpen={true}
        onClose={() => {}} // In public mode, we don't want to close
        spaceId={spaceId} // Pass as INTEGER
        mode="public"
      />
    </div>
  );
};

export default PublicSpaceRoute;
